import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popover } from "@mantine/core";
import {
  Appointment,
  AppointmentStatus,
} from "../../../interfaces/services/Appointment";
import {
  parseFixedISOStringToDate,
  toFixedISOString,
} from "../../../utils/dateUtils";
import { useDisclosure } from "@mantine/hooks";
import appointmentService from "../../../services/api/appointmentService";
import { toast } from "react-toastify";
import { TransactionReq } from "../../../interfaces/services/Finance";
import { useAtomValue } from "jotai";
import { meAtom } from "../../../store/auth";
import financeService from "../../../services/api/financeService";
import { getStatusColor } from "../../../pages/profile/Appointment/AppointmentsCalendarPage";
import AppointmentDetailsModal from "./AppointmentDetailsModal";

type TimeStripDailyCompactProps = {
  start: string;
  end: string;
  dayDate: Date;
  minuteHeight?: number;
  showHourGrid?: boolean;
  gridEveryMinutes?: number;
  className?: string;
  appointments: Appointment[];
  groupPermissions: any;
  fetchAppointments: () => Promise<void>;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const GUTTER_PX = 60;

function isSameLocalDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function parseHHmmToMinutes(hhmm: string): number {
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr),
    m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59)
    throw new Error(`Geçersiz saat: "${hhmm}" (HH:mm)`);
  return h * 60 + m;
}
function minutesToHHmm(total: number): string {
  total = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
}
function hourNumberText(total: number): string {
  const norm = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:00`;
}
function parseDateToMinutes(date: Date): number {
  if (!(date instanceof Date) || Number.isNaN(date.getTime()))
    throw new Error("Geçersiz Date nesnesi");
  return date.getHours() * 60 + date.getMinutes();
}
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function formatDayHeader(d: Date) {
  const wd = d.toLocaleDateString("tr-TR", { weekday: "short" });
  const dm = d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
  });
  return { wd, dm };
}
function fmtTime(d: Date) {
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/* ================= Group types & helpers ================= */
type DailyRawItem = {
  appt: Appointment;
  visibleStartMin: number;
  visibleEndMin: number;
  segStart: Date;
  segEnd: Date;
};

type DailyGroup = {
  items: DailyRawItem[];
  groupStartMin: number;
  groupEndMin: number;
};

function computeDailyGroups(
  appointments: Appointment[],
  day0: number,
  day1: number,
  slotStart: number,
  slotEnd: number,
): DailyGroup[] {
  const raw: DailyRawItem[] = [];

  for (const a of appointments) {
    try {
      const apptStart = parseFixedISOStringToDate(
        a.appointmentTime.appointmentStartDate,
      );
      const apptEnd = parseFixedISOStringToDate(
        a.appointmentTime.appointmentEndDate,
      );
      const segStartMs = Math.max(apptStart.getTime(), day0);
      const segEndMs = Math.min(apptEnd.getTime(), day1 - 1);
      if (segStartMs >= segEndMs) continue;

      const segStart = new Date(segStartMs);
      const segEnd = new Date(segEndMs);
      const visibleStartMin = clamp(
        parseDateToMinutes(segStart),
        slotStart,
        slotEnd,
      );
      const visibleEndMin = clamp(
        parseDateToMinutes(segEnd),
        slotStart,
        slotEnd,
      );
      if (visibleEndMin <= visibleStartMin) continue;

      raw.push({ appt: a, visibleStartMin, visibleEndMin, segStart, segEnd });
    } catch {
      /* skip invalid */
    }
  }

  raw.sort((a, b) => a.visibleStartMin - b.visibleStartMin);

  // BFS overlap grouping — transitively overlapping appointments in one group
  const assigned = new Set<number>();
  const groups: DailyGroup[] = [];

  for (let i = 0; i < raw.length; i++) {
    if (assigned.has(i)) continue;
    const groupItems: DailyRawItem[] = [raw[i]];
    assigned.add(i);
    const queue = [i];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (let j = 0; j < raw.length; j++) {
        if (assigned.has(j)) continue;
        if (
          raw[cur].visibleStartMin < raw[j].visibleEndMin &&
          raw[cur].visibleEndMin > raw[j].visibleStartMin
        ) {
          groupItems.push(raw[j]);
          assigned.add(j);
          queue.push(j);
        }
      }
    }
    const groupStartMin = Math.min(...groupItems.map((x) => x.visibleStartMin));
    const groupEndMin = Math.max(...groupItems.map((x) => x.visibleEndMin));
    groups.push({ items: groupItems, groupStartMin, groupEndMin });
  }

  return groups;
}

/* ================= Main Component ================= */
export default function TimeStripDaily({
  start,
  end,
  dayDate,
  minuteHeight = 2,
  showHourGrid = true,
  gridEveryMinutes = 30,
  className = "",
  appointments,
  groupPermissions,
  fetchAppointments,
}: TimeStripDailyCompactProps) {
  const me = useAtomValue(meAtom);

  const [now, setNow] = useState(new Date());
  const isToday = isSameLocalDay(dayDate, now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const nowRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);

  const startMin = parseHHmmToMinutes(start);
  const rawEndMin = parseHHmmToMinutes(end);
  const endAbs = rawEndMin >= startMin ? rawEndMin : rawEndMin + 1440;
  const effectiveEnd = Math.min(endAbs, 1440);
  const duration = Math.max(0, effectiveEnd - startMin);
  const heightPx = duration * minuteHeight;

  const firstFullHour = Math.ceil(startMin / 60) * 60;
  const hourMarks: number[] = [];
  for (let t = firstFullHour; t <= effectiveEnd; t += 60)
    if (t !== startMin) hourMarks.push(t);
  const endIsOnHour = effectiveEnd % 60 === 0;

  const minorGrid: number[] = useMemo(() => {
    const lines: number[] = [];
    if (gridEveryMinutes && gridEveryMinutes > 0) {
      const firstMinor =
        Math.ceil(startMin / gridEveryMinutes) * gridEveryMinutes;
      for (let t = firstMinor; t < effectiveEnd; t += gridEveryMinutes) {
        if (!(showHourGrid && t % 60 === 0)) lines.push(t);
      }
    }
    return lines;
  }, [gridEveryMinutes, startMin, effectiveEnd, showHourGrid]);

  const topOf = (t: number) => (t - startMin) * minuteHeight;
  const heightOf = (mins: number) => mins * minuteHeight;

  const showNow =
    isToday && nowMinutes >= startMin && nowMinutes <= effectiveEnd;

  /* ====== Popup state ====== */
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);

  /* ====== Groups ====== */
  const dailyGroups = useMemo(() => {
    const d0 = startOfLocalDay(dayDate).getTime();
    const d1 = d0 + MS_PER_DAY;
    return computeDailyGroups(appointments, d0, d1, startMin, effectiveEnd);
  }, [appointments, dayDate, startMin, effectiveEnd]);

  function RightGridOverlay() {
    return (
      <div className="pointer-events-none absolute inset-0 z-0">
        {showHourGrid &&
          hourMarks.map((t) => (
            <div
              key={`hline-${t}`}
              className="absolute left-0 right-0 h-px bg-background-lightAlt2 dark:bg-background-darkAlt2"
              style={{ top: `${topOf(t)}px` }}
            />
          ))}
        {minorGrid.map((t) => (
          <div
            key={`mline-${t}`}
            className="absolute left-0 right-0 h-px bg-background-lightAlt2/30 dark:bg-background-darkAlt2/50"
            style={{ top: `${topOf(t)}px` }}
          />
        ))}
      </div>
    );
  }

  function LeftTimeGutter() {
    return (
      <div
        className="relative shrink-0 border-r border-background-lightAlt2 dark:border-background-darkAlt2 px-3"
        style={{ width: GUTTER_PX, height: heightPx }}
      >
        <div
          className="absolute left-3 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300"
          style={{ top: 0 }}
        >
          {minutesToHHmm(startMin)}
        </div>
        {hourMarks
          .filter((t) => (endIsOnHour ? t <= effectiveEnd : t < effectiveEnd))
          .map((t) => (
            <div
              key={`hour-${t}`}
              className="absolute left-3 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300"
              style={{ top: `${topOf(t)}px` }}
              title={minutesToHHmm(t)}
            >
              {hourNumberText(t)}
            </div>
          ))}
        {!endIsOnHour && (
          <div
            className="absolute left-3 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300"
            style={{ top: `${topOf(effectiveEnd)}px` }}
          >
            {minutesToHHmm(rawEndMin % 1440)}
          </div>
        )}
      </div>
    );
  }

  /* ===== Actions ===== */
  const changeStatus = useCallback(
    async (a: Appointment, status: AppointmentStatus) => {
      try {
        await appointmentService.changestatus(a.code, status);

        if (status === "Completed") {
          const price = a.serviceSnapshot?.price || 0;
          const _date = new Date();
          const transaction: TransactionReq = {
            salonCode: me?.salonCode!,
            type: "Income",
            category: "Appointment",
            status: true,
            title: `Oto Randevu Ödemesi`,
            amount: price,
            date: toFixedISOString(_date),
            note: `SalonCepte tarafından otomatik olarak oluşturuldu`,
            employeeCode: a.employeeCode,
            employeeSnapshot: {
              name: me?.name!,
              lastname: me?.lastname!,
            },
          };
          await financeService.createTransaction(transaction);
          toast.success("Randevu Güncellendi ve Gelir kaydı oluşturuldu", {
            toastId: "change-status-success-with-income",
          });
        } else {
          toast.success("Randevu Güncellendi", {
            toastId: "change-status-success",
          });
        }
        fetchAppointments();
      } catch (error) {
        toast.error("Randevu Güncellenemedi", {
          toastId: "change-status-error",
        });
        console.error(error);
      }
    },
    [me, fetchAppointments],
  );

  /* ====== Modal state ====== */
  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<Appointment | null>(null);

  const canUpdate =
    groupPermissions.updateAppointmentStatus ||
    groupPermissions.updateAppointmentStatusAll ||
    groupPermissions.Receptionist;

  /* ====== Tek randevu kartı ====== */
  function renderSingleItem(item: DailyRawItem) {
    const { appt: a, visibleStartMin, visibleEndMin, segStart, segEnd } = item;
    const top = topOf(visibleStartMin);
    const height = heightOf(visibleEndMin - visibleStartMin);
    const isCompact = height < 48;

    const startLabel = fmtTime(segStart);
    const endLabel = fmtTime(segEnd);

    const sessionLabel =
      a.serviceSnapshot?.type === "Session"
        ? a.sessionNumber && a.serviceSnapshot?.sessionCount
          ? `${a.sessionNumber}/${a.serviceSnapshot.sessionCount}`
          : a.sessionNumber
            ? `#${a.sessionNumber}`
            : null
        : null;

    return (
      <div
        key={`apt-${a.code}`}
        className="absolute z-10 rounded-md cursor-pointer shadow-sm"
        style={{
          top: `${top}px`,
          height: `${height}px`,
          left: "2px",
          right: "2px",
          background: getStatusColor(a.status),
        }}
        title={`${minutesToHHmm(visibleStartMin)} - ${minutesToHHmm(
          visibleEndMin,
        )}`}
        onClick={() => {
          setSelected(a);
          open();
        }}
      >
        <div className="text-white px-2 py-0.5 h-full flex flex-col justify-center overflow-hidden">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-sm whitespace-nowrap">
              {startLabel}–{endLabel}
            </span>
            {sessionLabel && (
              <span className="text-[10px] font-bold bg-white/25 px-1.5 py-0.5 rounded leading-none shrink-0">
                {sessionLabel}
              </span>
            )}
          </div>
          {!isCompact && a.customerSnapshot && (
            <div className="flex items-center gap-1.5 text-[11px] opacity-90 min-w-0 mt-0.5">
              <span className="truncate font-medium">
                {a.customerSnapshot.name} {a.customerSnapshot.lastname}
              </span>
              <span className="w-px h-3 bg-white/40 shrink-0" />
              <span className="truncate">
                {a.employeeSnapshot?.name ?? "Personel"}{" "}
                {a.employeeSnapshot?.lastname}
              </span>
            </div>
          )}
          {!isCompact && a.serviceSnapshot && height > 72 && (
            <div className="text-[10px] opacity-60 truncate mt-0.5">
              {a.serviceSnapshot.name}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ====== Çakışan randevular — merged blok + popover ====== */
  function renderMergedGroup(group: DailyGroup, idx: number) {
    const { items, groupStartMin, groupEndMin } = group;
    const top = topOf(groupStartMin);
    const height = heightOf(groupEndMin - groupStartMin);
    const isCompact = height < 48;

    const groupKey = items
      .map((i) => i.appt.code)
      .sort()
      .join("-");
    const isOpen = expandedGroupKey === groupKey;

    const earliest = items.reduce((a, b) =>
      a.visibleStartMin <= b.visibleStartMin ? a : b,
    );
    const latest = items.reduce((a, b) =>
      a.visibleEndMin >= b.visibleEndMin ? a : b,
    );
    const startLabel = fmtTime(earliest.segStart);
    const endLabel = fmtTime(latest.segEnd);

    return (
      <Popover
        key={`grp-${idx}`}
        opened={isOpen}
        onChange={(o) => setExpandedGroupKey(o ? groupKey : null)}
        position="top-start"
        withArrow
        shadow="md"
        closeOnClickOutside
        zIndex={200}
        middlewares={{ flip: { fallbackPlacements: ["bottom-start"] } }}
      >
        <Popover.Target>
          <div
            className="absolute z-10 rounded-md cursor-pointer shadow-sm overflow-hidden"
            style={{
              top: `${top}px`,
              height: `${height}px`,
              left: "2px",
              right: "2px",
            }}
            onClick={() => setExpandedGroupKey(isOpen ? null : groupKey)}
          >
            {/* Sol durum şeridi */}
            <div className="absolute left-0 top-0 bottom-0 w-1.5 flex flex-col z-10">
              {items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    backgroundColor: getStatusColor(item.appt.status),
                  }}
                />
              ))}
            </div>

            {/* Arka plan */}
            <div className="absolute inset-0 bg-slate-700 dark:bg-slate-800" />

            {/* İçerik */}
            <div className="relative ml-1.5 px-2 py-1 h-full flex flex-col justify-center">
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-sm whitespace-nowrap">
                  {startLabel}–{endLabel}
                </span>
                <span className="text-[11px] font-bold text-white bg-white/20 px-1.5 py-0.5 rounded-full leading-none">
                  {items.length}
                </span>
              </div>
              {!isCompact && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-white/60 text-[11px]">
                    {items.length} randevu
                  </span>
                  <div className="flex gap-0.5">
                    {items.slice(0, 8).map((item, i) => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: getStatusColor(item.appt.status),
                        }}
                      />
                    ))}
                    {items.length > 8 && (
                      <span className="text-white/40 text-[9px] self-center ml-0.5">
                        +{items.length - 8}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Popover.Target>

        <Popover.Dropdown p="xs" style={{ minWidth: 240, maxWidth: 320 }}>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-1 mb-2">
            {items.length} randevu · {startLabel}–{endLabel}
          </p>
          <div className="flex flex-col gap-0.5">
            {items
              .slice()
              .sort((a, b) => a.visibleStartMin - b.visibleStartMin)
              .map((item, i) => {
                const s = fmtTime(item.segStart);
                const e = fmtTime(item.segEnd);
                return (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 px-2 py-2 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                    onClick={() => {
                      setExpandedGroupKey(null);
                      setSelected(item.appt);
                      open();
                    }}
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0 mt-0.5"
                      style={{
                        backgroundColor: getStatusColor(item.appt.status),
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                        {s}–{e}
                        {item.appt.serviceSnapshot && (
                          <span className="font-normal text-gray-500 dark:text-gray-400 ml-1.5">
                            {item.appt.serviceSnapshot.name}
                          </span>
                        )}
                      </div>
                      {item.appt.customerSnapshot ? (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {item.appt.customerSnapshot.name}{" "}
                          {item.appt.customerSnapshot.lastname}
                        </div>
                      ) : (
                        <div className="text-[11px] text-gray-400 italic">
                          Müşteri belirtilmemiş
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </Popover.Dropdown>
      </Popover>
    );
  }

  function CurrentTimeIndicator() {
    if (!showNow) return null;
    const top = (nowMinutes - startMin) * minuteHeight;
    return (
      <div
        ref={nowRef}
        className="absolute left-0 right-0 z-20 select-none"
        style={{ top }}
      >
        <div className="absolute -left-3 -translate-y-1/2 top-1/2">
          <div className="w-0 h-0 border-y-6 border-y-transparent border-r-6 border-r-rose-500 dark:border-r-rose-400" />
        </div>
        <div className="h-[2px] w-full bg-rose-500/90 dark:bg-rose-400/90 shadow-[0_0_0_1px_rgba(0,0,0,.05)]" />
        <div className="absolute -translate-y-1/2 top-1/2 left-0">
          <span className="block w-2.5 h-2.5 rounded-full bg-rose-500 dark:bg-rose-400" />
          <span className="block w-2.5 h-2.5 rounded-full bg-rose-500/40 dark:bg-rose-400/40 animate-ping absolute inset-0" />
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, [isToday]);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [dayDate, start, end]);

  useEffect(() => {
    if (!showNow || hasAutoScrolledRef.current) return;
    requestAnimationFrame(() => {
      nowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      hasAutoScrolledRef.current = true;
    });
  }, [showNow]);

  /* ====== Render ====== */
  return (
    <>
      <div
        className={[
          "overflow-hidden rounded-2xl border border-background-lightAlt2 dark:border-background-darkAlt2 bg-background-light dark:bg-background-dark shadow-sm w-full",
          className,
        ].join(" ")}
      >
        {/* Header */}
        <div className="relative z-10 flex border-b border-background-lightAlt2 dark:border-background-darkAlt2 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur">
          <div className="shrink-0" style={{ width: GUTTER_PX }} />
          {(() => {
            const { wd, dm } = formatDayHeader(dayDate);
            return (
              <div className="px-3 py-3 text-gray-900 dark:text-gray-100 border-r last:border-r-0 border-background-lightAlt2 dark:border-background-darkAlt2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  {wd}
                </div>
                <div className="text-sm font-semibold">{dm}</div>
              </div>
            );
          })()}
        </div>

        <div className="flex py-4 mb-8">
          <LeftTimeGutter />
          <div className="relative w-full" style={{ height: heightPx }}>
            <RightGridOverlay />
            <CurrentTimeIndicator />
            <div className="relative w-full h-full">
              {dailyGroups.map((group, idx) =>
                group.items.length === 1
                  ? renderSingleItem(group.items[0])
                  : renderMergedGroup(group, idx),
              )}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <AppointmentDetailsModal
          opened={opened}
          onClose={() => {
            close();
            setSelected(null);
          }}
          a={selected}
          canAct={{ canUpdate: canUpdate }}
          changeStatus={changeStatus}
        />
      )}
    </>
  );
}
